// Landing page, sign-in, registration and beta-access flows.
import { SUPABASE_KEY, SUPABASE_URL, TURNSTILE_SITE_KEY } from "../config.js";
import { loadAccount } from "../data/account.js";
import { bindLanguage, copy, languageControl, t } from "../i18n.js";
import { readApiResponse, readableError } from "../lib/api.js";
import { app, escapeHTML, setBusy, showModal, showToast } from "../lib/dom.js";
import { render } from "../router.js";
import { state } from "../state.js";
import { supabase } from "../supabase-client.js";
import { navigation } from "../ui/layout.js";

export function authPage(activeTab = "login") {
  const hasInvite = Boolean(state.betaInviteToken);
  const isLogin = activeTab !== "register" || !hasInvite;
  const it = state.language === "it";
  app.innerHTML = `
    <main class="landing-shell">
      <header class="landing-header">
        <a class="landing-brand" href="#inizio" aria-label="${it ? "BrainMath, torna all’inizio" : "BrainMath, back to top"}">
          <img src="/brainmath-logo.png" alt="">
          <span><strong>BrainMath</strong><small>${it ? "Impara. Cresci. Divertiti." : "Learn. Grow. Have fun."}</small></span>
        </a>
        <nav class="landing-nav" aria-label="${it ? "Navigazione principale" : "Main navigation"}">
          <a href="#come-funziona">${it ? "Come funziona" : "How it works"}</a>
          <a href="#per-chi">${it ? "Per chi" : "For whom"}</a>
          <a href="#sicurezza">${it ? "Sicurezza" : "Safety"}</a>
        </nav>
        <div class="landing-header-actions">
          ${languageControl()}
          <a class="landing-login-link" href="#accesso">${t("login")}</a>
        </div>
      </header>

      <section class="landing-hero-section" id="inizio">
        <div class="landing-hero-copy">
          <span class="landing-kicker"><i>✦</i> ${it ? "Matematica che cresce con ogni bambino" : "Math that grows with every child"}</span>
          <h1>${it ? "Ogni esercizio diventa un <em>piccolo traguardo.</em>" : "Every exercise becomes a <em>small achievement.</em>"}</h1>
          <p>${it
            ? "BrainMath unisce percorsi personalizzati, attività della classe e progressi visibili in uno spazio sicuro pensato per bambini, famiglie e insegnanti."
            : "BrainMath brings personalised learning paths, class activities and visible progress together in a safe space for children, families and teachers."}</p>
          <div class="landing-hero-actions">
            <a class="landing-primary-cta" href="#accesso">${it ? "Accedi a BrainMath" : "Sign in to BrainMath"} <span>→</span></a>
            <a class="landing-secondary-cta" href="#come-funziona">${it ? "Scopri come funziona" : "See how it works"}</a>
          </div>
          <div class="landing-trust-row" aria-label="${it ? "Caratteristiche principali" : "Key features"}">
            <span><b>✓</b>${it ? "Italiano e inglese" : "Italian and English"}</span>
            <span><b>✓</b>${it ? "Progressi condivisi" : "Shared progress"}</span>
            <span><b>✓</b>${it ? "Accesso controllato" : "Controlled access"}</span>
          </div>
        </div>

        <div class="landing-hero-visual" aria-label="${it ? "La famiglia BrainMath impara insieme" : "The BrainMath family learns together"}">
          <div class="landing-image-frame landing-character-stage">
            <span class="landing-scene-symbol symbol-plus" aria-hidden="true">+</span>
            <span class="landing-scene-symbol symbol-star" aria-hidden="true">★</span>
            <span class="landing-scene-symbol symbol-number" aria-hidden="true">7</span>
            <img src="/brainmath-scenes/character-family-support.webp?v=20260725-cutouts" alt="${it ? "La famiglia BrainMath affronta insieme una sfida di matematica" : "The BrainMath family tackles a mathematics challenge together"}">
          </div>
          <div class="landing-float-card landing-float-progress"><span>⭐</span><div><strong>+120 XP</strong><small>${it ? "Nuovo traguardo" : "New achievement"}</small></div></div>
          <div class="landing-float-card landing-float-class"><span>📚</span><div><strong>${it ? "Classe pronta" : "Class ready"}</strong><small>${it ? "Attività assegnate" : "Activities assigned"}</small></div></div>
        </div>
      </section>

      <section class="landing-access-section" id="accesso">
        <div class="landing-access-heading">
          <span class="landing-section-kicker">${it ? "Accesso BrainMath" : "BrainMath access"}</span>
          <h2>${hasInvite
            ? (it ? "Il tuo invito è pronto." : "Your invitation is ready.")
            : (it ? "Entra nel tuo spazio o richiedi la beta." : "Enter your space or request beta access.")}</h2>
          <p>${hasInvite
            ? (it ? "Puoi accedere con un account esistente oppure completare la registrazione collegata al tuo invito." : "Sign in with an existing account or complete the registration linked to your invitation.")
            : (it ? "Le registrazioni pubbliche sono chiuse. Chi possiede già un account può accedere; i nuovi utenti possono inviare una richiesta." : "Public registration is closed. Existing users can sign in, while new users can submit an access request.")}</p>
        </div>
        <div class="landing-access-grid ${hasInvite ? "has-invite" : ""}">
          <div class="landing-access-overview">
            <div class="access-overview-icon">${hasInvite ? "✉️" : "🔐"}</div>
            <span class="access-overview-label">${hasInvite ? (it ? "Invito personale verificato" : "Personal invitation verified") : (it ? "Beta privata e controllata" : "Controlled private beta")}</span>
            <h3>${hasInvite
              ? (it ? "Completa il tuo ingresso in BrainMath." : "Complete your BrainMath access.")
              : (it ? "Non hai ancora un account?" : "Don’t have an account yet?")}</h3>
            <p>${hasInvite
              ? (it ? "La registrazione è riservata all’indirizzo e ai ruoli approvati. Dopo la conferma email potrai accedere normalmente." : "Registration is limited to the approved email and roles. After email confirmation, you can sign in normally.")
              : (it ? "Raccontaci se vuoi provare BrainMath come genitore, insegnante o entrambi. L’amministratore valuterà la richiesta e invierà un link personale." : "Tell us whether you would like to try BrainMath as a parent, teacher, or both. An administrator will review the request and send a personal link.")}</p>
            <div class="landing-access-points">
              <span><i>1</i>${it ? "Richiesta controllata dall’amministratore" : "Administrator-reviewed request"}</span>
              <span><i>2</i>${it ? "Invito personale via email" : "Personal invitation by email"}</span>
              <span><i>3</i>${it ? "Profili e ruoli protetti" : "Protected profiles and roles"}</span>
            </div>
            ${hasInvite
              ? `<div class="access-invite-status"><b>✓</b><span>${it ? "Invito rilevato: puoi usare la scheda Crea account." : "Invitation detected: use the Create account tab."}</span></div>`
              : `<button class="landing-primary-cta access-request-button" type="button" data-request-beta>${it ? "Richiedi accesso alla beta" : "Request beta access"} <span>→</span></button>`}
          </div>
          <div class="auth-card landing-auth-card">
            <div class="auth-top">
              <span class="auth-panel-badge">${hasInvite ? (it ? "Invito verificato" : "Verified invitation") : (it ? "Account esistente" : "Existing account")}</span>
              <span class="auth-security-mark" aria-hidden="true">🛡️</span>
            </div>
            ${hasInvite ? `<div class="tabs landing-auth-tabs">
              <button class="tab-button ${isLogin ? "active" : ""}" data-auth-tab="login">${t("login")}</button>
              <button class="tab-button ${!isLogin ? "active" : ""}" data-auth-tab="register">${t("register")}</button>
            </div>` : ""}
            <div class="auth-heading">
              <span class="auth-step-label">${isLogin ? (it ? "ACCESSO ACCOUNT" : "ACCOUNT SIGN IN") : (it ? "REGISTRAZIONE SU INVITO" : "INVITATION REGISTRATION")}</span>
              <h2>${isLogin ? (it ? "Bentornato." : "Welcome back.") : t("register")}</h2>
              <p>${isLogin
                ? (it ? "Usa email, username oppure le credenziali del profilo bambino." : "Use your email, username, or child profile credentials.")
                : (it ? "Completa i dati approvati nell’invito. I campi riservati verranno bloccati automaticamente." : "Complete the details approved in your invitation. Reserved fields will be locked automatically.")}</p>
            </div>
            ${isLogin ? loginForm() : registerForm()}
          </div>
        </div>
      </section>

      <section class="landing-proof-strip" aria-label="${it ? "BrainMath in sintesi" : "BrainMath at a glance"}">
        <div><strong>4</strong><span>${it ? "livelli di difficoltà" : "difficulty levels"}</span></div>
        <div><strong>2</strong><span>${it ? "lingue supportate" : "supported languages"}</span></div>
        <div><strong>1</strong><span>${it ? "spazio condiviso" : "shared learning space"}</span></div>
        <div><strong>∞</strong><span>${it ? "piccoli progressi" : "small achievements"}</span></div>
      </section>

      <section class="landing-section" id="come-funziona">
        <div class="landing-section-heading">
          <span class="landing-section-kicker">${it ? "Un percorso semplice" : "A simple journey"}</span>
          <h2>${it ? "Dalla prima missione ai progressi visibili." : "From the first mission to visible progress."}</h2>
          <p>${it ? "Ogni parte di BrainMath ha uno scopo chiaro e accompagna il bambino senza creare confusione." : "Every part of BrainMath has a clear purpose and guides each child without unnecessary complexity."}</p>
        </div>
        <div class="landing-steps">
          <article><span class="landing-step-number">01</span><div class="landing-step-scene character-cutout-stage learning-action-stage"><img src="/brainmath-scenes/landing-math-path-game-together.webp?v=20260728-learning-scenes" alt="${it ? "Un bambino e una bambina giocano su un percorso di numeri e simboli matematici" : "A boy and a girl play on a path of numbers and mathematical symbols"}" loading="lazy" decoding="async"><span>🎲</span></div><h3>${it ? "Scegli la missione" : "Choose a mission"}</h3><p>${it ? "Il percorso propone attività adatte all’età e permette di scegliere tra Base, Medio, Avanzato ed Esperto." : "The path offers age-appropriate activities across Base, Medium, Advanced and Expert levels."}</p></article>
          <article><span class="landing-step-number">02</span><div class="landing-step-scene character-cutout-stage learning-action-stage"><img src="/brainmath-scenes/landing-guided-learning.webp?v=20260728-guided-learning" alt="${it ? "Una bambina svolge un’attività matematica con la supervisione di un adulto" : "A girl completes a maths activity with adult guidance"}" loading="lazy" decoding="async"><span>🤝</span></div><h3>${it ? "Impara facendo, con una guida accanto" : "Learn by doing, with guidance"}</h3><p>${it ? "I bambini provano, correggono e ricevono feedback immediati, mentre genitori e insegnanti seguono il percorso e intervengono quando serve." : "Children practise, correct mistakes and receive immediate feedback, while parents and teachers follow their journey and step in when needed."}</p></article>
          <article><span class="landing-step-number">03</span><div class="landing-step-scene character-cutout-stage family-cutout-stage"><img src="/brainmath-scenes/character-family-celebration.webp?v=20260725-cutouts" alt="${it ? "La famiglia BrainMath festeggia un traguardo" : "The BrainMath family celebrates an achievement"}" loading="lazy" decoding="async"><span>⭐</span></div><h3>${it ? "Celebra i progressi" : "Celebrate progress"}</h3><p>${it ? "XP, livelli e risultati mostrano ciò che è stato conquistato e aiutano adulti e bambini a capire il passo successivo." : "XP, levels and results show what has been achieved and help adults and children see the next step."}</p></article>
        </div>
      </section>

      <section class="landing-section landing-audience-section" id="per-chi">
        <div class="landing-section-heading">
          <span class="landing-section-kicker">${it ? "Un ecosistema, tre esperienze" : "One ecosystem, three experiences"}</span>
          <h2>${it ? "La schermata giusta per chi la sta usando." : "The right experience for every role."}</h2>
        </div>
        <div class="landing-audience-grid">
          <article class="landing-audience-card child"><div class="audience-emoji">🚀</div><span>${it ? "Bambini" : "Children"}</span><h3>${it ? "Un’avventura da completare" : "An adventure to complete"}</h3><p>${it ? "Missioni, difficoltà progressive, ricompense e una grafica adatta all’età, dalle elementari alle medie." : "Missions, progressive difficulty, rewards and age-aware visuals from primary to middle school."}</p><ul><li>${it ? "Percorso personale" : "Personal journey"}</li><li>${it ? "Compiti della classe" : "Class assignments"}</li><li>${it ? "Progressi e traguardi" : "Progress and achievements"}</li></ul></article>
          <article class="landing-audience-card family"><div class="audience-emoji">🏡</div><span>${it ? "Famiglie" : "Families"}</span><h3>${it ? "Ogni figlio, il suo profilo" : "A profile for every child"}</h3><p>${it ? "Il genitore crea e protegge gli account dei figli, controlla i progressi e mantiene scuola e classe aggiornate." : "Parents create and protect child accounts, follow progress and keep school and class details current."}</p><ul><li>${it ? "Più profili bambino" : "Multiple child profiles"}</li><li>${it ? "Accesso rapido come figlio" : "Quick child access"}</li><li>${it ? "Password gestite dal genitore" : "Parent-managed passwords"}</li></ul></article>
          <article class="landing-audience-card teacher"><div class="audience-emoji">🏫</div><span>${it ? "Insegnanti" : "Teachers"}</span><h3>${it ? "Classi e contenuti in ordine" : "Classes and content, organised"}</h3><p>${it ? "Crea, revisiona e assegna esercizi per classe, oppure parti dal catalogo condiviso senza rigenerare contenuti." : "Create, review and assign class activities, or reuse the shared catalogue without regenerating content."}</p><ul><li>${it ? "Generazione AI con revisione" : "Reviewed AI generation"}</li><li>${it ? "Catalogo per classe e argomento" : "Catalogue by grade and topic"}</li><li>${it ? "Progressi degli alunni" : "Learner progress"}</li></ul></article>
        </div>
      </section>

      <section class="landing-product-section">
        <div class="landing-product-copy">
          <span class="landing-section-kicker">${it ? "Apprendimento leggibile" : "Learning made visible"}</span>
          <h2>${it ? "Non una lista infinita di esercizi. Un percorso che ha senso." : "Not an endless exercise list. A journey that makes sense."}</h2>
          <p>${it ? "I contenuti sono organizzati per ordine scolastico, classe, area matematica, argomento e difficoltà. Il bambino vede solo ciò che gli serve; insegnanti e amministratori usano la stessa gerarchia." : "Content is organised by school stage, grade, maths area, topic and difficulty. Children see what matters to them while teachers and administrators use the same hierarchy."}</p>
          <div class="landing-feature-pills"><span>🌱 Base</span><span>⭐ Medio</span><span>🚀 Avanzato</span><span>🏆 Esperto</span></div>
        </div>
        <div class="landing-product-demo" aria-hidden="true">
          <div class="demo-top"><span>Il sentiero delle stelle</span><b>Livello 4</b></div>
          <div class="demo-path">
            <i class="complete">✓</i><span></span><i class="complete">✓</i><span></span><i class="current">★</i><span></span><i>4</i>
          </div>
          <div class="demo-mission"><div><span>➕</span><p><small>${it ? "Prossima missione" : "Next mission"}</small><strong>${it ? "Il ponte dei numeri" : "The number bridge"}</strong></p></div><b>+80 XP</b></div>
        </div>
      </section>

      <section class="landing-safety-section" id="sicurezza">
        <div class="landing-safety-mark">🛡️</div>
        <div>
          <span class="landing-section-kicker">${it ? "Beta privata e controllata" : "Controlled private beta"}</span>
          <h2>${it ? "Prima la sicurezza, poi la crescita." : "Safety first, then growth."}</h2>
          <p>${it ? "Le registrazioni pubbliche sono disattivate. Ogni richiesta viene verificata e l’account viene creato soltanto attraverso un invito personale." : "Public registration is disabled. Every request is reviewed and accounts can only be created through a personal invitation."}</p>
        </div>
        <div class="landing-safety-list">
          <span><i>✓</i>${it ? "Profili bambino senza email obbligatoria" : "Child profiles without mandatory email"}</span>
          <span><i>✓</i>${it ? "Ruoli e permessi separati" : "Separated roles and permissions"}</span>
          <span><i>✓</i>${it ? "Contenuti revisionabili dall’insegnante" : "Teacher-reviewable content"}</span>
          <span><i>✓</i>${it ? "Accesso beta approvato dall’amministratore" : "Administrator-approved beta access"}</span>
        </div>
      </section>

      <footer class="landing-footer">
        <div class="landing-brand"><img src="/brainmath-logo.png" alt=""><span><strong>BrainMath</strong><small>${it ? "Matematica, passo dopo passo." : "Mathematics, step by step."}</small></span></div>
        <p>© ${new Date().getFullYear()} BrainMath Game · ${it ? "Beta privata" : "Private beta"}</p>
        <a href="#inizio">${it ? "Torna su ↑" : "Back to top ↑"}</a>
      </footer>
    </main>`;
  const landingAccessSection = document.querySelector("#accesso");
  const landingSafetySection = document.querySelector("#sicurezza");
  if (landingAccessSection && landingSafetySection) {
    landingSafetySection.insertAdjacentElement("afterend", landingAccessSection);
  }
  bindLanguage();
  document.querySelectorAll("[data-auth-tab]").forEach(button => {
    button.addEventListener("click", () => authPage(button.dataset.authTab));
  });
  document.querySelectorAll("[data-request-beta]").forEach(button => {
    button.addEventListener("click", betaAccessRequestModal);
  });
  isLogin ? bindLogin() : bindRegistration();
}

export function verificationPage() {
  const email = state.verificationEmail || new URLSearchParams(location.search).get("email") || "";
  app.innerHTML = `
    <main class="auth-shell">
      <section class="auth-hero">
        <img class="brand-logo" src="/brainmath-logo.png" alt="BrainMath">
        <h1>BrainMath</h1>
        <p>${t("tagline")}</p>
      </section>
      <section class="auth-panel">
        <div class="auth-card verification-card">
          <div class="auth-top">${languageControl()}</div>
          <div class="verification-icon" aria-hidden="true">✉️</div>
          <h2>${state.language === "it" ? "Verifica la tua email" : "Verify your email"}</h2>
          <p>${state.language === "it"
            ? `Abbiamo inviato un link di conferma a <strong>${escapeHTML(email)}</strong>. Apri l’email e conferma l’account prima di accedere.`
            : `We sent a confirmation link to <strong>${escapeHTML(email)}</strong>. Open the email and confirm your account before signing in.`}</p>
          <p class="verification-help">${state.language === "it"
            ? "Se non la trovi, controlla anche la cartella Spam."
            : "If you cannot find it, also check your Spam folder."}</p>
          <button id="verification-login" class="primary full" type="button">${state.language === "it" ? "Torna all’accesso" : "Back to login"}</button>
        </div>
      </section>
    </main>`;
  bindLanguage();
  document.querySelector("#verification-login").addEventListener("click", () => {
    state.verificationEmail = null;
    sessionStorage.removeItem("brainmath-verification-email");
    history.replaceState({}, "", "/");
    authPage("login");
  });
}

export function publicMaintenancePage() {
  document.documentElement.lang = state.language;
  app.innerHTML = `
    <main class="auth-shell">
      <section class="auth-hero">
        <img class="brand-logo" src="/brainmath-logo.png" alt="BrainMath">
        <h1>BrainMath</h1>
        <p>${state.language === "it"
          ? "Stiamo preparando la beta privata."
          : "We are preparing the private beta."}</p>
        <div class="feature-list">
          <div class="feature"><span class="feature-icon">✦</span>${state.language === "it" ? "Accesso solo su invito" : "Invite-only access"}</div>
          <div class="feature"><span class="feature-icon">♟</span>${state.language === "it" ? "Registrazioni pubbliche disattivate" : "Public registrations disabled"}</div>
          <div class="feature"><span class="feature-icon">↗</span>${state.language === "it" ? "Sviluppo in corso" : "Development in progress"}</div>
        </div>
      </section>
      <section class="auth-panel">
        <div class="auth-card verification-card">
          <div class="auth-top">${languageControl()}</div>
          <div class="verification-icon" aria-hidden="true">🚧</div>
          <h2>${state.language === "it" ? "BrainMath non è ancora pubblico" : "BrainMath is not public yet"}</h2>
          <p>${state.language === "it"
            ? "Il sito è temporaneamente offline per gli utenti esterni mentre completiamo sicurezza, contenuti e beta privata."
            : "The website is temporarily offline for external users while we complete security, content and the private beta."}</p>
          <p class="verification-help">${state.language === "it"
            ? "Se hai ricevuto un invito, riprova più avanti quando la beta sarà aperta."
            : "If you received an invitation, please try again when the beta opens."}</p>
        </div>
      </section>
    </main>`;
  bindLanguage();
}

function loginForm() {
  return `<form id="login-form" class="form-grid">
    <label>${t("identifier")}<input name="identifier" autocomplete="username" required></label>
    <label>${t("password")}<input name="password" type="password" autocomplete="current-password" minlength="8" required></label>
    <button class="primary turquoise full" type="submit">${t("login")}</button>
    <button id="forgot-password" class="link-button" type="button">${t("forgot")}</button>
    <p id="auth-message" class="form-message"></p>
  </form>`;
}

function betaAccessRequestModal() {
  showModal(`
    <div class="modal-header">
      <div><span class="eyebrow">BrainMath Beta</span><h2>${state.language === "it" ? "Richiedi accesso" : "Request access"}</h2></div>
      <button class="close-button" data-close-modal>×</button>
    </div>
    <p style="color:var(--muted);margin-top:0">${state.language === "it"
      ? "La richiesta verrà controllata dall’amministratore. Se approvata, riceverai un invito personale via email."
      : "An administrator will review your request. If approved, you will receive a personal invitation by email."}</p>
    <form id="beta-access-form" class="form-grid">
      <div class="form-row">
        <label>${state.language === "it" ? "Nome" : "First name"}<input name="firstName" autocomplete="given-name" maxlength="80" required></label>
        <label>${state.language === "it" ? "Cognome" : "Last name"}<input name="lastName" autocomplete="family-name" maxlength="80" required></label>
      </div>
      <label>Email<input name="email" type="email" autocomplete="email" maxlength="254" required></label>
      <fieldset class="form-grid" style="border:0;padding:0;margin:0">
        <legend style="font-weight:800;margin-bottom:8px">${state.language === "it" ? "Accesso richiesto" : "Requested access"}</legend>
        <label class="check-row"><input name="requestedRoles" type="checkbox" value="parent"><span>${state.language === "it" ? "Genitore" : "Parent"}</span></label>
        <label class="check-row"><input name="requestedRoles" type="checkbox" value="teacher"><span>${state.language === "it" ? "Insegnante" : "Teacher"}</span></label>
      </fieldset>
      <div id="beta-teacher-fields" class="form-grid" hidden>
        <div class="form-row">
          <label>${state.language === "it" ? "Scuola principale" : "Primary school"}<input name="schoolName" maxlength="120"></label>
          <label>${state.language === "it" ? "Comune" : "City"}<input name="schoolCity" maxlength="120"></label>
        </div>
      </div>
      <label>${state.language === "it" ? "Perché vuoi provare BrainMath?" : "Why would you like to try BrainMath?"}<textarea name="motivation" rows="4" maxlength="800"></textarea></label>
      <label class="check-row"><input name="privacy" type="checkbox" required><span>${state.language === "it" ? "Accetto che questi dati siano usati per valutare la richiesta beta." : "I agree that this data may be used to review my beta request."}</span></label>
      <button class="primary full" type="submit">${state.language === "it" ? "Invia richiesta" : "Send request"}</button>
      <p class="form-message"></p>
    </form>`);
  const form = document.querySelector("#beta-access-form");
  const teacherRole = form.querySelector('input[value="teacher"]');
  const teacherFields = form.querySelector("#beta-teacher-fields");
  teacherRole.addEventListener("change", () => {
    teacherFields.hidden = !teacherRole.checked;
    teacherFields.querySelectorAll("input").forEach(input => { input.required = teacherRole.checked; });
  });
  form.addEventListener("submit", async event => {
    event.preventDefault();
    const button = form.querySelector('button[type="submit"]');
    const message = form.querySelector(".form-message");
    const values = new FormData(form);
    const requestedRoles = values.getAll("requestedRoles");
    if (!requestedRoles.length) {
      message.textContent = state.language === "it" ? "Seleziona almeno un tipo di accesso." : "Select at least one access type.";
      return;
    }
    setBusy(button, true);
    try {
      const response = await fetch(`${SUPABASE_URL}/functions/v1/beta-access`, {
        method: "POST",
        headers: { "Content-Type": "application/json", apikey: SUPABASE_KEY },
        body: JSON.stringify({
          action: "request",
          email: String(values.get("email") || "").trim(),
          first_name: String(values.get("firstName") || "").trim(),
          last_name: String(values.get("lastName") || "").trim(),
          requested_roles: requestedRoles,
          school_name: String(values.get("schoolName") || "").trim(),
          school_city: String(values.get("schoolCity") || "").trim(),
          motivation: String(values.get("motivation") || "").trim(),
          language: state.language,
        }),
      });
      const result = await readApiResponse(response, state.language === "it" ? "Richiesta non inviata." : "Request not sent.");
      showModal(`<div class="modal-header"><h2>${state.language === "it" ? "Richiesta ricevuta" : "Request received"}</h2><button class="close-button" data-close-modal>×</button></div><div class="verification-icon" aria-hidden="true">✉️</div><p>${escapeHTML(result.message)}</p><button class="primary full" data-close-modal>${state.language === "it" ? "Torna all’accesso" : "Back to login"}</button>`);
    } catch (error) {
      message.textContent = readableError(error, state.language === "it" ? "Richiesta non inviata." : "Request not sent.");
      setBusy(button, false);
    }
  });
}

function registerForm() {
  const hasInvite = Boolean(state.betaInviteToken);
  return `<form id="register-form" class="form-grid">
    <input name="betaInviteToken" type="hidden" value="${escapeHTML(state.betaInviteToken)}">
    <fieldset class="registration-group">
      <legend>${state.language === "it" ? "Credenziali" : "Credentials"}</legend>
      <div class="form-row">
        <label>${t("email")}<input name="email" type="email" autocomplete="email" required></label>
        <label>${t("username")}<input name="username" pattern="[A-Za-z0-9_]{3,24}" autocomplete="username" required></label>
      </div>
      <label>${t("password")}<input name="password" type="password" minlength="8" autocomplete="new-password" required></label>
    </fieldset>
    <fieldset class="registration-group">
      <legend>${state.language === "it" ? "Profilo" : "Profile"}</legend>
      <div class="form-row">
        <label>${t("birthDate")}<input name="birthDate" type="date" required></label>
        <label>${t("country")}<input name="country" value="IT" maxlength="2" pattern="[A-Za-z]{2}" required></label>
      </div>
      <div class="registration-role-grid" aria-label="${t("role")}">
        <label class="registration-role"><input name="roles" type="checkbox" value="parent" checked><span><b>🏡</b><strong>${t("parent")}</strong></span></label>
        <label class="registration-role"><input name="roles" type="checkbox" value="teacher"><span><b>🏫</b><strong>${t("teacher")}</strong></span></label>
      </div>
    </fieldset>
    <div id="teacher-school-fields" class="registration-group form-grid" hidden>
      <strong class="registration-group-title">${state.language === "it" ? "Scuola principale" : "Primary school"}</strong>
      <div class="form-row">
        <label>${state.language === "it" ? "Nome scuola" : "School name"}<input name="teacherSchoolName"></label>
        <label>${state.language === "it" ? "Comune" : "City"}<input name="teacherSchoolCity"></label>
      </div>
      <p class="registration-help">${state.language === "it" ? "Dopo l’accesso potrai aggiungere altre scuole e creare le classi." : "After signing in you can add more schools and create classes."}</p>
    </div>
    <label class="check-row"><input name="accept" type="checkbox" required><span>${t("accept")}</span></label>
    <div id="turnstile-container" ${TURNSTILE_SITE_KEY ? "" : "hidden"}></div>
    <button class="primary full" type="submit" ${hasInvite ? "" : "disabled"}>${t("register")}</button>
    <p id="auth-message" class="form-message"></p>
  </form>`;
}

function loadTurnstileScript() {
  return new Promise((resolve, reject) => {
    if (!TURNSTILE_SITE_KEY) return resolve();
    if (window.turnstile) return resolve();
    const existing = document.querySelector('script[data-turnstile-script="true"]');
    if (existing) {
      existing.addEventListener("load", resolve, { once: true });
      existing.addEventListener("error", reject, { once: true });
      return;
    }
    const script = document.createElement("script");
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    script.async = true;
    script.defer = true;
    script.dataset.turnstileScript = "true";
    script.addEventListener("load", resolve, { once: true });
    script.addEventListener("error", reject, { once: true });
    document.head.append(script);
  });
}

async function renderTurnstile() {
  state.turnstileToken = "";
  if (!TURNSTILE_SITE_KEY) return;
  const container = document.querySelector("#turnstile-container");
  if (!container) return;
  container.hidden = false;
  container.innerHTML = "";
  await loadTurnstileScript();
  if (!window.turnstile) return;
  window.turnstile.render(container, {
    sitekey: TURNSTILE_SITE_KEY,
    callback: token => { state.turnstileToken = token; },
    "expired-callback": () => { state.turnstileToken = ""; },
    "error-callback": () => { state.turnstileToken = ""; },
  });
}

function bindLogin() {
  const form = document.querySelector("#login-form");
  const message = document.querySelector("#auth-message");
  form.addEventListener("submit", async event => {
    event.preventDefault();
    const button = form.querySelector("button[type=submit]");
    setBusy(button);
    message.textContent = "";
    const values = new FormData(form);
    try {
      const response = await fetch(`${SUPABASE_URL}/functions/v1/sign-in`, {
        method: "POST",
        headers: { "Content-Type": "application/json", apikey: SUPABASE_KEY },
        body: JSON.stringify({
          identifier: values.get("identifier").trim(),
          password: values.get("password")
        })
      });
      const session = await response.json();
      if (!response.ok) throw new Error(session.message || "Unable to sign in.");
      const { error } = await supabase.auth.setSession({
        access_token: session.access_token,
        refresh_token: session.refresh_token
      });
      if (error) throw error;
      state.session = session;
      await loadAccount();
      render();
    } catch (error) {
      message.textContent = error.message;
    } finally {
      setBusy(button, false);
    }
  });

  document.querySelector("#forgot-password").addEventListener("click", () => resetPasswordModal());
  document.querySelector("#request-beta-access")?.addEventListener("click", betaAccessRequestModal);
}

async function loadBetaInviteDetails(form) {
  if (!state.betaInviteToken) return;
  try {
    const response = await fetch(`${SUPABASE_URL}/functions/v1/beta-access`, {
      method: "POST",
      headers: { "Content-Type": "application/json", apikey: SUPABASE_KEY },
      body: JSON.stringify({ action: "invite_details", invite_token: state.betaInviteToken }),
    });
    const details = await readApiResponse(response, state.language === "it" ? "Invito non valido o scaduto." : "Invalid or expired invitation.");
    state.betaInviteDetails = details;
    const emailInput = form.elements.email;
    if (details.email) {
      emailInput.value = details.email;
      emailInput.readOnly = true;
    }
    const allowedRoles = Array.isArray(details.allowed_roles) ? details.allowed_roles : [];
    form.querySelectorAll('input[name="roles"]').forEach(input => {
      input.checked = allowedRoles.includes(input.value);
      input.disabled = !allowedRoles.includes(input.value);
    });
    form.querySelector('input[name="roles"][value="teacher"]')?.dispatchEvent(new Event("change"));
  } catch (error) {
    state.betaInviteDetails = null;
    localStorage.removeItem("brainmath-beta-invite");
    state.betaInviteToken = "";
    history.replaceState({}, "", location.pathname);
    showToast(readableError(error, state.language === "it" ? "Invito non valido o scaduto." : "Invalid or expired invitation."));
    authPage("login");
  }
}

function bindRegistration() {
  const form = document.querySelector("#register-form");
  const message = document.querySelector("#auth-message");
  renderTurnstile().catch(() => {
    message.textContent = state.language === "it"
      ? "Controllo sicurezza non disponibile. Ricarica la pagina."
      : "Security check is not available. Reload the page.";
  });
  const teacherRole = form.querySelector('input[name="roles"][value="teacher"]');
  const teacherFields = form.querySelector("#teacher-school-fields");
  teacherRole.addEventListener("change", () => {
    teacherFields.hidden = !teacherRole.checked;
    teacherFields.querySelectorAll("input").forEach(input => { input.required = teacherRole.checked; });
  });
  loadBetaInviteDetails(form);
  form.addEventListener("submit", async event => {
    event.preventDefault();
    const button = form.querySelector("button[type=submit]");
    setBusy(button);
    message.textContent = "";
    const values = new FormData(form);
    const birthDate = new Date(values.get("birthDate"));
    const age = new Date().getFullYear() - birthDate.getFullYear();
    const roles = values.getAll("roles");
    if (age < 14) {
      message.textContent = state.language === "it"
        ? "La registrazione diretta è disponibile dai 14 anni. Per i bambini deve registrarsi un genitore."
        : "Direct registration is available from age 14. A parent must register for younger children.";
      setBusy(button, false);
      return;
    }
    if (!roles.length) {
      message.textContent = state.language === "it" ? "Seleziona almeno un profilo." : "Select at least one role.";
      setBusy(button, false);
      return;
    }
    if (!values.get("betaInviteToken")) {
      message.textContent = state.language === "it"
        ? "Registrazione disponibile solo con link di invito beta."
        : "Registration is available only with a beta invitation link.";
      setBusy(button, false);
      return;
    }
    if (TURNSTILE_SITE_KEY && !state.turnstileToken) {
      message.textContent = state.language === "it"
        ? "Completa il controllo sicurezza prima di registrarti."
        : "Complete the security check before registering.";
      setBusy(button, false);
      return;
    }
    try {
      const response = await fetch(`${SUPABASE_URL}/functions/v1/register-or-merge`, {
        method: "POST",
        headers: { "Content-Type": "application/json", apikey: SUPABASE_KEY },
        body: JSON.stringify({
          email: values.get("email").trim(),
          username: values.get("username").trim(),
          password: values.get("password"),
          birth_date: values.get("birthDate"),
          country_code: values.get("country").toUpperCase(),
          roles,
          privacy_version: "2026-06-19",
          terms_version: "2026-06-19",
          beta_invite_token: values.get("betaInviteToken"),
          turnstile_token: state.turnstileToken,
          redirect_to: `${location.origin}/auth/confirmed`
          ,
          teacher_schools: roles.includes("teacher") ? [{
            name: values.get("teacherSchoolName").trim(),
            city: values.get("teacherSchoolCity").trim(),
            country_code: values.get("country").toUpperCase()
          }] : []
        })
      });
      const result = await readApiResponse(
        response,
        state.language === "it"
          ? "Registrazione non riuscita. Riprova tra poco."
          : "Registration failed. Please try again shortly."
      );
      if (result.merged && result.session) {
        const { error } = await supabase.auth.setSession({
          access_token: result.session.access_token,
          refresh_token: result.session.refresh_token
        });
        if (error) throw error;
        state.session = result.session;
        localStorage.removeItem("brainmath-beta-invite");
        state.betaInviteToken = "";
        history.replaceState({}, "", location.pathname);
        await loadAccount();
        showToast(state.language === "it"
          ? "I nuovi ruoli sono stati uniti allo stesso account."
          : "The new roles were merged into the same account.");
        render();
        return;
      }
      message.style.color = "var(--turquoise)";
      localStorage.removeItem("brainmath-beta-invite");
      state.betaInviteToken = "";
      state.verificationEmail = values.get("email").trim();
      sessionStorage.setItem("brainmath-verification-email", state.verificationEmail);
      form.reset();
      history.pushState({}, "", `/verify-email?email=${encodeURIComponent(state.verificationEmail)}`);
      render();
    } catch (error) {
      const errorMessage = readableError(
        error,
        state.language === "it"
          ? "Registrazione non riuscita. Riprova tra poco."
          : "Registration failed. Please try again shortly."
      );
      message.textContent = errorMessage.toLowerCase().includes("already")
        ? (state.language === "it"
          ? "Questo account esiste già. Accedi con la stessa login e aggiungi il nuovo ruolo dalla sezione Account."
          : "This account already exists. Sign in with the same login and add the new role from Account.")
        : errorMessage;
    } finally {
      setBusy(button, false);
    }
  });
}

function resetPasswordModal() {
  showModal(`
    <div class="modal-header"><h2>${t("forgot")}</h2><button class="close-button" data-close-modal>×</button></div>
    <form id="reset-form" class="form-grid">
      <label>${t("email")}<input name="email" type="email" required></label>
      <button class="primary full" type="submit">${t("forgot")}</button>
      <p class="form-message"></p>
    </form>`);
  const form = document.querySelector("#reset-form");
  form.addEventListener("submit", async event => {
    event.preventDefault();
    const button = form.querySelector("button");
    setBusy(button);
    const email = new FormData(form).get("email");
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${location.origin}/auth/reset-password`
    });
    const message = form.querySelector(".form-message");
    message.textContent = error ? error.message : t("resetSent");
    message.style.color = error ? "var(--coral)" : "var(--turquoise)";
    setBusy(button, false);
  });
}
